import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Legend
} from "chart.js";
import InfoIcon from "../../../assets/icons/info-icon.svg";
import {
  formatNumberToCurrency
} from "../../../utils/formatter";
import FinanceCardChart from "../financeCardChart/financeCardChart";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Legend);

const FinanceInvoicing = ({ received = [], billed = [], proportionReceivedIssued = [], code }) => {
  const [showPopover, setShowPopover] = useState(false);
  const [showDataModal, setShowDataModal] = useState(false);
  const [clickedMonthData, setClickedMonthData] = useState(null); // Armazena { raw, formatted }
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const chartRef = useRef(null);
  const navigate = useNavigate();

  const largerArray = received.length >= billed.length ? received : billed;
  const data = {
    labels: largerArray.map((agreement) => agreement.month),
    datasets: [
      {
        label: "Faturado",
        data: billed.map((agreement) => agreement.value),
        borderColor: "#0962AD",
        backgroundColor: "#0962AD",
        borderWidth: 2
      },
      {
        label: "Recebido",
        data: received.map((agreement) => agreement.value),
        borderColor: "#2BAD70",
        backgroundColor: "#2BAD70",
        borderWidth: 2
      },
      {
        label: "% de Recebimento médio",
        data: proportionReceivedIssued,
        borderColor: "#FFA500",
        backgroundColor: "#FFA500",
        borderWidth: 2,
        yAxisID: "y1",
      }
    ]
  };

  const handleChartClick = (event, elements) => {
    if (elements && elements.length > 0) {
      const clickedElement = elements[0];
      const datasetIndex = clickedElement.datasetIndex;
      
      // Verificar se clicou no dataset "Faturado" (índice 0) ou "Recebido" (índice 1)
      if (datasetIndex === 0 || datasetIndex === 1) {
        const pointIndex = clickedElement.index;
        const monthRaw = largerArray[pointIndex].month;
        
        console.log('Mês clicado (raw):', monthRaw);
        
        setClickedMonthData({
          raw: monthRaw,
          formatted: formatMonthDisplay(monthRaw)
        });
        setModalPosition({
          x: event.native.clientX,
          y: event.native.clientY
        });
        setShowDataModal(true);
      }
    }
  };

  const formatMonthDisplay = (monthStr) => {
    if (!monthStr) return '';
    
    // Se já vier no formato esperado (ex: "04/2025"), retorna direto
    if (monthStr.includes('/')) {
      return monthStr;
    }
    
    // Caso contrário, tenta fazer o parse do formato "YYYY-MM"
    const parts = monthStr.split('-');
    if (parts.length === 2) {
      const [year, month] = parts;
      const monthNames = [
        'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
      ];
      const monthIndex = parseInt(month) - 1;
      if (monthIndex >= 0 && monthIndex < 12) {
        return `${monthNames[monthIndex]}/${year}`;
      }
    }
    
    // Fallback: retorna o valor original
    return monthStr;
  };

  const handleNavigateToData = () => {
    if (!clickedMonthData) {
      setShowDataModal(false);
      return;
    }
    
    const monthRaw = clickedMonthData.raw;
    let year, month;
    
    // Mapeamento de meses abreviados para números
    const monthMap = {
      'jan': '01', 'fev': '02', 'mar': '03', 'abr': '04',
      'mai': '05', 'jun': '06', 'jul': '07', 'ago': '08',
      'set': '09', 'out': '10', 'nov': '11', 'dez': '12'
    };
    
    // Formato esperado: "YYYY-MM" (ex: "2025-04")
    if (monthRaw.includes('-') && monthRaw.split('-').length === 2) {
      const parts = monthRaw.split('-');
      const firstPart = parts[0];
      const secondPart = parts[1];
      
      // Verificar se o primeiro é ano (4 dígitos) ou mês
      if (firstPart.length === 4) {
        // Formato YYYY-MM
        year = firstPart;
        month = secondPart.padStart(2, '0');
      } else {
        // Formato possível MM-YYYY
        month = firstPart.padStart(2, '0');
        year = secondPart;
      }
    } else if (monthRaw.includes('/')) {
      // Formato "mar/25" ou "MM/YY" ou "MM/YYYY"
      const parts = monthRaw.split('/');
      if (parts.length === 2) {
        const firstPart = parts[0].toLowerCase().trim();
        const secondPart = parts[1].trim();
        
        // Verificar se é mês abreviado
        if (monthMap[firstPart]) {
          month = monthMap[firstPart];
          // Converter ano de 2 dígitos para 4
          year = secondPart.length === 2 ? `20${secondPart}` : secondPart;
        } else if (!isNaN(firstPart)) {
          // É um número (MM/YYYY)
          month = firstPart.padStart(2, '0');
          year = secondPart.length === 2 ? `20${secondPart}` : secondPart;
        }
      }
    }
    
    if (year && month) {
      console.log('Parseando data:', { monthRaw, year, month });
      
      // Calcular o último dia do mês
      const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
      
      console.log('Último dia calculado:', lastDay);
      
      // Formato YYYY-MM-DD para os filtros
      const startDateISO = `${year}-${month}-01`;
      const endDateISO = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
      
      console.log('Datas ISO geradas:', { startDateISO, endDateISO });
      
      // Construir URL com filtros estruturados
      const params = new URLSearchParams();
      params.set('page', '1');
      
      // Filtro 0: ReceivedValue > 0
      params.set('filter0_field', 'ReceivedValue');
      params.set('filter0_operator', 'gt');
      params.set('filter0_values', JSON.stringify(["0"]));
      params.set('filter0_groupId', '0');
      
      // Filtro 1: ReceivedDate >= startDate
      params.set('filter1_field', 'ReceivedDate');
      params.set('filter1_operator', 'gte');
      params.set('filter1_values', JSON.stringify([startDateISO]));
      params.set('filter1_groupId', '0');
      params.set('filter1_condition', 'AND');
      
      // Filtro 2: ReceivedDate <= endDate
      params.set('filter2_field', 'ReceivedDate');
      params.set('filter2_operator', 'lte');
      params.set('filter2_values', JSON.stringify([endDateISO]));
      params.set('filter2_groupId', '0');
      params.set('filter2_condition', 'AND');
      
      navigate(`/app/dados-consolidados?${params.toString()}`);
    } else {
      console.error('Não foi possível parsear a data:', monthRaw);
    }
    
    setShowDataModal(false);
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: handleChartClick,
    plugins: {
      legend: {
        position: "top",
        align: "end",
        labels: {
          boxHeight: 6,
          boxWidth: 10,
          color: "#858585",
          font: { size: 14 }
        }
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const value = context.raw;
            const label = context.dataset.label;

            if (label.includes("% de Recebimento médio")) {
              return `${label}: ${value.toFixed(2)}%`;
            }

            return `${label}: ${formatNumberToCurrency(value)}`;
          }
        }
      }

    },
    scales: {
      x: { grid: { display: false } },
      y: {
        ticks: {
          callback: function (value) {
            return formatNumberToCurrency(value);
          }
        },
        position: "left",
        title: {
          display: true,
          text: "Valores (R$)"
        }
      },
      y1: {
        position: "right",
        grid: { drawOnChartArea: false },
        ticks: {
          callback: function (value) {
            return `${value.toFixed(0)}%`;
          }
        },
        title: {
          display: true,
          text: "Proporção (%)"
        }
      }
    }

  };

  return (
    <>
      <FinanceCardChart
        code={code}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>Desconto médio sobre o Faturado</span>
            <div style={{ position: 'relative' }}>
              <img
                src={InfoIcon}
                alt="Informação"
                onClick={() => setShowPopover(!showPopover)}
                style={{
                  width: '14px',
                  height: '14px',
                  cursor: 'pointer',
                  verticalAlign: 'middle',
                  display: 'inline-block',
                }}
              />
              {showPopover && (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "100%",
                    transform: "translate(0, -50%)",
                    marginLeft: "8px",
                    backgroundColor: "#fff",
                    border: "1px solid #ccc",
                    padding: "10px",
                    zIndex: 100,
                    width: "220px",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    borderRadius: "4px"
                  }}
                >
                  <p style={{ margin: 0, fontSize: "12px", fontWeight: "normal" }}>
                    O Faturado representa o valor total de serviços com início no mês, independentemente do pagamento. O Recebido soma os pagamentos realizados no mês, mesmo que de meses anteriores.                </p>
                  <button
                    onClick={() => setShowPopover(false)}
                    style={{
                      marginTop: "8px",
                      fontSize: "10px",
                      cursor: "pointer",
                      background: "none",
                      border: "none",
                      color: "#0962AD"
                    }}
                  >
                    Fechar
                  </button>
                </div>
              )}
            </div>
          </div>
        }
      >
        <Line ref={chartRef} data={data} options={options} />
      </FinanceCardChart>

      {showDataModal && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999
            }}
            onClick={() => setShowDataModal(false)}
          />
          <div
            style={{
              position: "fixed",
              top: modalPosition.y + 10,
              left: modalPosition.x - 100,
              backgroundColor: "#fff",
              padding: "12px 16px",
              borderRadius: "6px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              border: "1px solid #e0e0e0",
              zIndex: 1000,
              minWidth: "200px",
              maxWidth: "250px"
            }}
          >
            <p style={{ margin: "0 0 12px 0", fontSize: "13px", color: "#333", fontWeight: "500" }}>
              {clickedMonthData?.formatted || ''}
            </p>
            <button
              onClick={handleNavigateToData}
              style={{
                width: "100%",
                padding: "6px 12px",
                fontSize: "13px",
                cursor: "pointer",
                backgroundColor: "#0962AD",
                border: "none",
                borderRadius: "4px",
                color: "#fff"
              }}
            >
              Ver dados consolidados
            </button>
          </div>
        </>
      )}
    </>
  );
};

export default FinanceInvoicing;
